/**
 * A service that will share the current page, or a provided URL, on
 * a supported platform.
 */

class ShareService {
  /**
   * Shares the current page or provided URL on a social platform.
   *
   * @param {string} platform
   *  The social platform to share on.
   * @param {object} options
   *  Options to override sharing defaults.
   */
  share(platform, options = {}) {
    // Provide some default options.
    const defaultOptions = {
      shareUrl: window.location.href,
      newWindow: {
        width: 575,
        height: 279,
      },
      shareInfo: {
        base: "",
        title: document.title,
        params: {},
      },
    };

    // Combine the default options with any overriding options passed in.
    const platformOptions = Object.assign({}, defaultOptions, options);

    // The data structure will be slightly different depending on the platform.
    switch (platform) {
      case "facebook":
        platformOptions.newWindow.title = "Facebook";
        platformOptions.shareInfo = {
          base: "https://www.facebook.com/share.php",
          params: {
            u: encodeURI(platformOptions.shareUrl),
          },
        };

        break;

      case "twitter":
        platformOptions.newWindow.title = "Twitter";
        platformOptions.newWindow.width = 550;
        platformOptions.newWindow.height = 420;
        platformOptions.shareInfo = {
          base: "https://twitter.com/intent/tweet",
          params: {
            url: encodeURI(platformOptions.shareUrl),
            text: this._formatText(platformOptions),
          },
        };

        break;

      case "linkedin":
        platformOptions.newWindow.title = "LinkedIn";
        platformOptions.shareInfo = {
          base: "https://www.linkedin.com/shareArticle",
          params: {
            mini: "true",
            url: encodeURI(platformOptions.shareUrl),
            title: platformOptions.text || platformOptions.shareInfo.title,
            summary: "",
            source: "",
          },
        };

        break;

      case "copy":
        // eslint-disable-next-line no-case-declarations
        const dummy = document.createElement("input"),
          text = window.location.href;

        document.body.appendChild(dummy);
        dummy.value = text;
        dummy.select();
        document.execCommand("copy");
        document.body.removeChild(dummy);
        return;

      case "email":
        // Email is a special case. Here we just change the window location like
        // a link with a `mailto` would.
        window.location = `mailto:?subject=${platformOptions.shareInfo.title}&body=${platformOptions.shareUrl}`;
        return;
    }

    // Open the share window.
    this._openShareWindow(platformOptions);
  }

  /**
   * Format text to be sent to platform
   *
   * @param options
   * @private
   */
  _formatText(options) {
    let text = options.text || options.shareInfo.title;
    if (options.mentions) {
      options.mentions.forEach(mention => {
        text += " @" + mention;
      });
    }
    if (options.hashtags) {
      options.hashtags.forEach(hashtag => {
        text += " #" + hashtag;
      });
    }
    return text;
  }

  /**
   * Opens a new window for sharing on a social platform.
   *
   * @param platformOptions
   *
   * @returns {Window}
   *
   * @private
   */
  _openShareWindow(platformOptions) {
    // Create any URL parameters to append to the URL.
    const sharePlatformUrlParams = new URLSearchParams();
    if (platformOptions.shareInfo.params) {
      for (const name in platformOptions.shareInfo.params) {
        sharePlatformUrlParams.append(
          name,
          platformOptions.shareInfo.params[name]
        );
      }
    }

    // Build the url for the share window.
    const sharePlatformUrl = `${
      platformOptions.shareInfo.base
    }?${sharePlatformUrlParams.toString()}`;

    // Calculate the position of the share window.
    const shareWindowPosition = {
      top: (window.innerHeight - platformOptions.newWindow.height) / 2,
      left: (window.innerWidth - platformOptions.newWindow.width) / 2,
    };

    // Open the share window.
    return window.open(
      sharePlatformUrl,
      platformOptions.newWindow.title,
      `status = 1, 
      resizeable = 0,
      width = ${platformOptions.newWindow.width},
      height = ${platformOptions.newWindow.height},
      top = ${shareWindowPosition.top},
      left = ${shareWindowPosition.left}`
    );
  }
}

export default ShareService;
